import { ThemeConfig } from 'antd';
import { brand } from '@/brand';

/**
 * Base z-index for every antd portal (Select/Dropdown/Tooltip popups,
 * Modal, message, notification).
 *
 * Antd's default is 1000, but this app still has hand-rolled overlays in
 * plain CSS that sit at z-index 10000-10001 (`.modal-overlay` in
 * AdminPage.css / SkillModal.css, `.fpm-overlay` in FilePreviewModal.css).
 * With the default base, any antd popup opened from inside one of those
 * overlays renders BEHIND it — a Select dropdown silently never appears and
 * an error toast is invisible, so the UI looks frozen.
 *
 * Antd derives popup z-indexes from this base (dropdowns at +50, message at
 * +10), so keeping it above the legacy overlay layer fixes all of them at
 * once. It is the --z-popover step of the stacking order in
 * styles/tokens/raw.css and must stay equal to it.
 */
export const Z_INDEX_POPUP_BASE = 10100;

/**
 * Dark Theme - Brand-tinted dark palette.
 * Semantic colors derived from brand where possible.
 */
export function getDarkTheme(): ThemeConfig {
  const bg = brand.darkMode;

  return {
    token: {
      // Keep antd popups above the legacy CSS overlays — see Z_INDEX_POPUP_BASE
      zIndexPopupBase: Z_INDEX_POPUP_BASE,

      // Primary — M² sky blue
      colorPrimary: brand.colors.primary,
      colorPrimaryHover: brand.colors.primaryHover,
      colorPrimaryActive: brand.colors.secondaryDark,

      // Semantic — exact M² brandbook palette
      colorSuccess: '#8BBF2A',    // M² green row
      colorWarning: '#FAB600',    // M² yellow — exact
      colorError: '#D50032',      // M² red — exact
      colorInfo: brand.colors.primary,

      // Backgrounds — deep ocean-tinted
      colorBgBase: bg?.bgPrimary ?? '#0b1018',
      colorBgContainer: bg?.bgSecondary ?? '#111a24',
      colorBgElevated: bg?.bgTertiary ?? '#17222e',
      colorBgLayout: bg?.bgPrimary ?? '#0b1018',

      // Text — slightly warm whites for readability on blue-dark
      colorText: '#eaf2f8',
      colorTextSecondary: '#b8cdd9',
      colorTextTertiary: '#7a95a8',
      colorTextQuaternary: '#506878',

      // Borders — subtle M² blue tint
      colorBorder: '#1e3040',
      colorBorderSecondary: '#2a3e50',

      // Border Radius
      borderRadius: 8,
      borderRadiusLG: 12,
      borderRadiusSM: 4,
      borderRadiusXS: 2,

      // Typography
      fontFamily: brand.fonts?.body
        ? `'${brand.fonts.body}', 'Ploni ML v2 AAA', 'Varela Round', -apple-system, BlinkMacSystemFont, sans-serif`
        : "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize: 16,
      fontSizeSM: 14,
      fontSizeLG: 18,
      fontSizeXL: 20,
      fontSizeHeading1: 30,
      fontSizeHeading2: 24,
      fontSizeHeading3: 20,
      fontSizeHeading4: 18,
      fontSizeHeading5: 16,

      // Spacing
      margin: 16,
      marginLG: 24,
      marginMD: 16,
      marginSM: 12,
      marginXS: 8,
      marginXXS: 4,

      // Shadows — cooler tint
      boxShadow: '0 4px 6px -1px rgba(8, 20, 32, 0.3)',
      boxShadowSecondary: '0 1px 2px 0 rgba(8, 20, 32, 0.25)',
      boxShadowTertiary: '0 10px 15px -3px rgba(8, 20, 32, 0.3)',
    },

    components: {
      Button: {
        primaryShadow: '0 4px 12px -2px rgba(108, 199, 239, 0.25)',
        controlHeight: 44,
        controlHeightLG: 52,
        controlHeightSM: 36,
        borderRadius: 8,
        borderRadiusLG: 12,
        borderRadiusSM: 4,
      },
      Input: {
        controlHeight: 44,
        controlHeightLG: 52,
        controlHeightSM: 36,
        borderRadius: 8,
        colorBgContainer: bg?.bgSecondary ?? '#111a24',
        colorBorder: '#1e3040',
      },
      Select: {
        controlHeight: 44,
        borderRadius: 8,
      },
      Card: {
        borderRadiusLG: 12,
        boxShadowTertiary: '0 4px 8px -2px rgba(8, 20, 32, 0.25)',
        colorBgContainer: bg?.bgSecondary ?? '#111a24',
        colorBorderSecondary: '#1e3040',
      },
      Layout: {
        siderBg: '#091018',
        headerBg: bg?.bgPrimary ?? '#0b1018',
        bodyBg: bg?.bgPrimary ?? '#0b1018',
        triggerBg: '#1e3040',
        triggerColor: '#eaf2f8',
      },
      Menu: {
        darkItemBg: '#091018',
        darkItemSelectedBg: '#1e3040',
        darkItemHoverBg: bg?.bgSecondary ?? '#111a24',
        itemBorderRadius: 8,
      },
      Modal: {
        contentBg: bg?.bgSecondary ?? '#111a24',
        headerBg: bg?.bgSecondary ?? '#111a24',
        borderRadiusLG: 12,
      },
      Message: {
        contentBg: bg?.bgSecondary ?? '#111a24',
        borderRadiusLG: 8,
      },
      Tooltip: {
        colorBgSpotlight: bg?.bgElevated ?? '#1c2935',
        borderRadius: 8,
      },
      Dropdown: {
        borderRadiusLG: 8,
        controlItemBgHover: '#1e3040',
      },
    },
  };
}

/**
 * Light Theme - Brand-tinted light palette.
 */
export function getLightTheme(): ThemeConfig {
  const bg = brand.lightMode;

  return {
    token: {
      // Keep antd popups above the legacy CSS overlays — see Z_INDEX_POPUP_BASE
      zIndexPopupBase: Z_INDEX_POPUP_BASE,

      // Primary — M² sky blue
      colorPrimary: '#4AABCE',  // slightly deeper for light bg contrast
      colorPrimaryHover: '#3A9ABF',
      colorPrimaryActive: brand.colors.secondaryDark,

      // Semantic — richer for light backgrounds
      colorSuccess: '#7AA625',   // M² green darker for light bg
      colorWarning: '#D49B00',   // M² yellow darker for light bg
      colorError: '#B8002A',     // M² red darker for light bg
      colorInfo: '#4AABCE',

      // Backgrounds — airy sky tint
      colorBgBase: bg?.bgPrimary ?? '#f6fafe',
      colorBgContainer: bg?.bgSecondary ?? '#ffffff',
      colorBgElevated: bg?.bgTertiary ?? '#edf5fb',
      colorBgLayout: bg?.bgPrimary ?? '#f6fafe',

      // Text — deep blue-grays for warmth
      colorText: '#0f2a3d',
      colorTextSecondary: '#2e4a5c',
      colorTextTertiary: '#5e7d8f',
      colorTextQuaternary: '#8ea8b8',

      // Borders — soft blue tint
      colorBorder: '#c8dce8',
      colorBorderSecondary: '#dbe9f0',

      // Border Radius
      borderRadius: 8,
      borderRadiusLG: 12,
      borderRadiusSM: 4,

      // Typography
      fontFamily: brand.fonts?.body
        ? `'${brand.fonts.body}', 'Ploni ML v2 AAA', 'Varela Round', -apple-system, BlinkMacSystemFont, sans-serif`
        : "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize: 16,
      fontSizeSM: 14,
      fontSizeLG: 18,

      // Spacing
      margin: 16,
      marginLG: 24,
      marginSM: 12,
      marginXS: 8,
      marginXXS: 4,

      // Shadows — subtle cool
      boxShadow: '0 4px 6px -1px rgba(15, 42, 61, 0.08)',
      boxShadowSecondary: '0 1px 2px 0 rgba(15, 42, 61, 0.05)',
      boxShadowTertiary: '0 10px 15px -3px rgba(15, 42, 61, 0.08)',
    },

    components: {
      Button: {
        primaryShadow: '0 4px 12px -2px rgba(74, 171, 206, 0.2)',
        controlHeight: 44,
        controlHeightLG: 52,
        controlHeightSM: 36,
      },
      Input: {
        controlHeight: 44,
      },
      Select: {
        controlHeight: 44,
      },
      Card: {
        borderRadiusLG: 12,
        boxShadowTertiary: '0 4px 8px -2px rgba(15, 42, 61, 0.06)',
      },
      Layout: {
        siderBg: bg?.bgSecondary ?? '#ffffff',
        headerBg: bg?.bgPrimary ?? '#f6fafe',
        bodyBg: bg?.bgPrimary ?? '#f6fafe',
      },
    },
  };
}
